import React, { useState } from 'react';
import { Cookie, Settings, X } from 'lucide-react';
import { useCookies } from '../contexts/CookieContext';

const CookieSettings = ({ onSave, onClose, currentPreferences }) => {
  const [preferences, setPreferences] = useState(currentPreferences);

  const handlePreferenceChange = (type, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Cookies nécessaires',
      description: 'Ces cookies sont indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.',
      required: true
    },
    {
      key: 'statistics',
      title: 'Cookies statistiques',
      description: 'Ces cookies nous permettent de mesurer l\'audience du site et d\'améliorer ses performances.',
      required: false
    },
    {
      key: 'marketing',
      title: 'Cookies marketing',
      description: 'Ces cookies permettent de personnaliser la publicité et les contenus en fonction de vos intérêts.',
      required: false
    },
    {
      key: 'external',
      title: 'Cookies tiers',
      description: 'Ces cookies proviennent de services externes (YouTube, réseaux sociaux, cartes, etc.).',
      required: false
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Settings className="w-6 h-6 text-[#4b9fcc] mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Paramètres des cookies</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Gérez vos préférences de cookies. Vous pouvez activer ou désactiver différents types de cookies selon vos préférences.
          </p>

          <div className="space-y-6">
            {cookieTypes.map((type) => (
              <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{type.title}</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[type.key]}
                      onChange={(e) => handlePreferenceChange(type.key, e.target.checked)}
                      disabled={type.required}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                      preferences[type.key] ? 'bg-[#4b9fcc]' : 'bg-gray-300'
                    } ${type.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                        preferences[type.key] ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`} />
                    </div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">{type.description}</p>
                {type.required && (
                  <p className="text-xs text-[#4b9fcc] mt-1 font-medium">Obligatoire</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-[#4b9fcc] text-white rounded-lg hover:bg-[#e71f74] transition-colors duration-300"
          >
            Enregistrer mes choix
          </button>
        </div>
      </div>
    </div>
  );
};

const CookieBanner = () => {
  const {
    showCookieBanner,
    setShowCookieBanner,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomPreferences,
    cookiePreferences
  } = useCookies();

  const [showSettings, setShowSettings] = useState(false);

  if (!showCookieBanner && !showSettings) {
    return null;
  }

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleSavePreferences = (preferences) => {
    saveCustomPreferences(preferences);
    setShowSettings(false);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
    setShowCookieBanner(false);
  };

  if (showSettings) {
    return (
      <CookieSettings
        onSave={handleSavePreferences}
        onClose={handleCloseSettings}
        currentPreferences={cookiePreferences}
      />
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          {/* Content */}
          <div className="flex items-start mb-4 lg:mb-0 lg:mr-8">
            <div className="flex-shrink-0 mr-4">
              <Cookie className="w-8 h-8 text-[#4b9fcc]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Gestion des cookies 🍪
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Nous utilisons des cookies pour assurer le bon fonctionnement du site, mesurer son audience et améliorer votre expérience.<br />
                Vous pouvez accepter tous les cookies, les refuser ou personnaliser vos choix.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 lg:flex-shrink-0">
            <button
              onClick={acceptAllCookies}
              className="px-4 py-2 bg-[#4b9fcc] text-white rounded-lg hover:bg-[#e71f74] transition-colors duration-300 font-medium text-sm"
            >
              Tout accepter
            </button>
            <button
              onClick={rejectAllCookies}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-300 font-medium text-sm"
            >
              Tout refuser
            </button>
            <button
              onClick={handleCustomize}
              className="px-4 py-2 border border-[#4b9fcc] text-[#4b9fcc] rounded-lg hover:bg-[#4b9fcc] hover:text-white transition-colors duration-300 font-medium text-sm flex items-center"
            >
              <Settings className="w-4 h-4 mr-2" />
              Personnaliser mes choix
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;